import clsx from 'clsx'
import numeral from 'numeral'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import React from 'react'
import { makeStyles } from '@material-ui/core'
import {
  LocationOnOutlined as LocationIcon,
  LocalDiningOutlined as LocalDiningIcon,
  ThumbUpOutlined as ThumbUpIcon,
} from '@material-ui/icons'
import { Link } from 'react-router-dom'

import { Typography } from 'src/modules/ui'
import { flattenAddress } from 'src/utils'

import SupplierActions from './SupplierActions'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    color: 'inherit',
    display: 'flex',
    flexDirection: 'row',
    maxWidth: 900,
    padding: theme.spacing(2, 2, 1),
    textDecoration: 'inherit',
    '&:hover': {
      backgroundColor: theme.palette.fadedGreen.light,
      cursor: 'pointer',
    },
  },
  actions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  address: {
    alignItems: 'center',
    color: theme.palette.darkGreen.main,
    display: 'flex',
    flexDirection: 'row',
  },
  addressIcon: {
    fontSize: '1rem',
  },
  body: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  details: {
    flexGrow: 1,
  },
  detailsLogoContainer: {
    display: 'flex',
  },
  description: {
    marginTop: theme.spacing(1.5),
    flexGrow: 1,
    display: 'box',
    lineClamp: 4,
    maxHeight: 96,
    boxOrient: 'vertical',
    overflow: 'hidden',
  },
  logo: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    display: 'none',
    flexShrink: 0,
    height: 120,
    justifyContent: 'center',
    marginRight: theme.spacing(4),
    objectFit: 'cover',
    textAlign: 'center',
    width: 120,
    [theme.breakpoints.up('mobile')]: {
      display: 'flex',
    },
  },
  logoMobile: {
    display: 'block',
    height: 70,
    marginRight: theme.spacing(1),
    objectFit: 'cover',
    width: 70,
    [theme.breakpoints.up('mobile')]: {
      display: 'none',
    },
  },
  descriptionMetaContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row-reverse',
    },
  },
  meta: {
    flexShrink: 0,
    [theme.breakpoints.up('tablet')]: {
      marginLeft: theme.spacing(2),
    },
  },
  metaItem: {
    alignItems: 'center',
    color: theme.palette.darkGreen.main,
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('tablet')]: {
      marginBottom: theme.spacing(1.5),
    },
    '&:first-child': {
      marginTop: theme.spacing(2),
      [theme.breakpoints.up('tablet')]: {
        marginTop: theme.spacing(1),
      },
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  metaIcon: {
    backgroundColor: theme.palette.lightGreen.main,
    borderRadius: '50%',
    color: 'white',
    padding: 2,
    marginRight: theme.spacing(1),
  },
  name: {
    color: theme.palette.orange.main,
    fontSize: '1.25rem',
    fontFamily: 'Gotham',
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      lineHeight: 1.5,
    },
  },
  reviewIcon: {
    padding: 4,
  },
}))

const SupplierCard = ({
  canMessage,
  canRequestAgreement,
  canStar,
  className,
  linkPrefix,
  onClick,
  ...supplier
}) => {
  const {
    address = {},
    description,
    distance,
    id,
    logo,
    matchingProducts,
    name,
    reviewCount,
    totalProducts,
  } = supplier
  const classes = useStyles()

  return (
    <Link
      className={clsx(classes.root, className)}
      onClick={onClick}
      to={`${linkPrefix}/suppliers/${id}`}
    >
      {logo ? (
        <img className={classes.logo} src={logo} alt={name} />
      ) : (
        <div className={classes.logo}>
          <Typography variant="body2" color="lightGreen">
            {name}
          </Typography>
        </div>
      )}
      <div className={classes.body}>
        <div className={classes.details}>
          <div className={classes.detailsLogoContainer}>
            <img className={classes.logoMobile} src={logo} alt={name} />
            <div>
              <Typography className={classes.name}>{name}</Typography>
              <Typography className={classes.address} variant="body2">
                <LocationIcon className={classes.addressIcon} />
                {flattenAddress(address)}
              </Typography>
            </div>
          </div>
          <div className={classes.descriptionMetaContainer}>
            <div className={classes.meta}>
              {distance != null && (
                <Typography className={classes.metaItem} variant="body2">
                  <LocationIcon className={classes.metaIcon} />
                  {numeral(distance).format('0.0')} miles away
                </Typography>
              )}
              {reviewCount > 0 && (
                <Typography className={classes.metaItem} variant="body2">
                  <ThumbUpIcon
                    className={clsx(classes.metaIcon, classes.reviewIcon)}
                  />
                  {pluralize('recommendation', reviewCount, true)}
                </Typography>
              )}
              {!!(matchingProducts && totalProducts) && (
                <Typography className={classes.metaItem} variant="body2">
                  <LocalDiningIcon className={classes.metaIcon} />
                  {matchingProducts} / {totalProducts} matching products
                </Typography>
              )}
            </div>
            <Typography className={classes.description}>
              {description}
            </Typography>
          </div>
        </div>
        <SupplierActions
          className={classes.actions}
          {...{ canMessage, canRequestAgreement, canStar }}
          {...supplier}
        />
      </div>
    </Link>
  )
}

SupplierCard.propTypes = {
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    county: PropTypes.string,
    postcode: PropTypes.string,
  }),
  canStar: PropTypes.bool,
  canMessage: PropTypes.bool,
  canRequestAgreement: PropTypes.bool,
  description: PropTypes.string,
  id: PropTypes.number,
  logo: PropTypes.string,
  // This is used to prefix public supplier URLs with /public
  linkPrefix: PropTypes.string,
  matchingProducts: PropTypes.number,
  proposalStatus: PropTypes.oneOf(['requested', 'proposed', 'accepted']),
  reviewCount: PropTypes.number,
  starred: PropTypes.bool,
  totalProducts: PropTypes.number,
}

SupplierCard.defaultProps = {
  canStar: true,
  canMessage: true,
  canRequestAgreement: true,
  linkPrefix: '',
}

export default SupplierCard
